/**
 * Centralized string utility functions
 * Provides consistent string manipulation across the Go Sit Back platform
 */
export class StringUtils {
  /**
   * Capitalize the first letter of a string
   * @param str - String to capitalize
   * @returns Capitalized string
   */
  static capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Capitalize the first letter of each word
   * @param str - String to title case
   * @returns Title cased string
   */
  static titleCase(str: string): string {
    return str.split(' ').map(word => this.capitalize(word)).join(' ');
  }

  /**
   * Truncate a string to a maximum length with ellipsis
   * @param str - String to truncate
   * @param maxLength - Maximum length
   * @param suffix - Suffix to add (defaults to '...')
   * @returns Truncated string
   */
  static truncate(str: string, maxLength: number, suffix: string = '...'): string {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength - suffix.length) + suffix;
  }

  /**
   * Convert string to kebab-case
   * @param str - String to convert
   * @returns Kebab-cased string
   */
  static kebabCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  }

  /**
   * Convert string to camelCase
   * @param str - String to convert
   * @returns CamelCased string
   */
  static camelCase(str: string): string {
    return str
      .toLowerCase()
      .replace(/[-_\s]+(.)?/g, (_, char) => char ? char.toUpperCase() : '');
  }

  /**
   * Convert string to PascalCase
   * @param str - String to convert
   * @returns PascalCased string
   */
  static pascalCase(str: string): string {
    const camelCased = this.camelCase(str);
    return camelCased.charAt(0).toUpperCase() + camelCased.slice(1);
  }

  /**
   * Convert string to snake_case
   * @param str - String to convert
   * @returns Snake_cased string
   */
  static snakeCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
  }

  /**
   * Validate email format
   * @param email - Email to validate
   * @returns True if email format is valid
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Remove extra whitespace and normalize spacing
   * @param str - String to normalize
   * @returns Normalized string
   */
  static normalizeWhitespace(str: string): string {
    return str.trim().replace(/\s+/g, ' ');
  }

  /**
   * Extract initials from a name
   * @param name - Full name
   * @param maxInitials - Maximum number of initials (defaults to 2)
   * @returns Initials string
   */
  static getInitials(name: string, maxInitials: number = 2): string {
    return name
      .split(' ')
      .filter(word => word.length > 0)
      .slice(0, maxInitials)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }

  /**
   * Generate a slug from text (URL-friendly)
   * @param text - Text to slugify
   * @returns URL-friendly slug
   */
  static slugify(text: string): string {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Escape HTML special characters
   * @param str - String to escape
   * @returns HTML-escaped string
   */
  static escapeHtml(str: string): string {
    const htmlEscapes: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;'
    };
    return str.replace(/[&<>"']/g, char => htmlEscapes[char]);
  }

  /**
   * Count words in a string
   * @param str - String to count words in
   * @returns Number of words
   */
  static wordCount(str: string): number {
    return str.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  /**
   * Generate a random string
   * @param length - Length of the string
   * @param charset - Character set to use (defaults to alphanumeric)
   * @returns Random string
   */
  static randomString(length: number, charset: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'): string {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  }
}